/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://statefulcoders.com/product/material-dashboard-react
* Copyright 2025 Stateful Coders (https://statefulcoders.com)

Coded by https://statefulcoders.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import Dashboard from "layouts/dashboard";
import Users from "layouts/users";
import Contest from "layouts/contest";
import Profile from "layouts/profile";
import SignIn from "layouts/authentication/sign-in";
import Settings from "layouts/settings";
import Applications from "layouts/applications";
import Support from "layouts/support";

// @mui icons
import Icon from "@mui/material/Icon";

import { Navigate } from "react-router-dom";
import PropTypes from "prop-types";
import Jobs from "layouts/jobs";
import SubscriptionPlans from "layouts/subscription-plans";
import PurchaseHistory from "layouts/purchase-history";
import Notifications from "layouts/notifications";

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/sign-in" />;
};

PrivateRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const PublicRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return !token ? children : <Navigate to="/dashboard" />;
};

PublicRoute.propTypes = {
  children: PropTypes.node.isRequired,
};

const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: (
      <PrivateRoute>
        <Dashboard />
      </PrivateRoute>
    ),
  },
  {
    type: "collapse",
    name: "Users Management",
    key: "users",
    icon: <Icon fontSize="small">people</Icon>,
    route: "/users",
    component: (
      <PrivateRoute>
        <Users />
      </PrivateRoute>
    ),
  },
  {
    type: "collapse",
    name: "Jobs Management",
    key: "jobs",
    icon: <Icon fontSize="small">work</Icon>,
    route: "/jobs",
    component: (
      <PrivateRoute>
        <Jobs />
      </PrivateRoute>
    ),
  },
  {
    type: "collapse",
    name: "Applications",
    key: "applications",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/applications",
    component: (
      <PrivateRoute>
        <Applications />
      </PrivateRoute>
    ),
  },
  {
    type: "collapse",
    name: "Support",
    key: "support",
    icon: <Icon fontSize="small">support_agent</Icon>,
    route: "/support",
    component: (
      <PrivateRoute>
        <Support />
      </PrivateRoute>
    ),
  },
  {
    type: "collapse",
    name: "Subscription Plans",
    key: "subscription-plans",
    icon: <Icon fontSize="small">card_membership</Icon>,
    route: "/subscription-plans",
    component: (
      <PrivateRoute>
        <SubscriptionPlans />
      </PrivateRoute>
    ),
  },
  {
    type: "collapse",
    name: "Purchase History",
    key: "purchase-history",
    icon: <Icon fontSize="small">history</Icon>,
    route: "/purchase-history",
    component: (
      <PrivateRoute>
        <PurchaseHistory />
      </PrivateRoute>
    ),
  },
  {
    type: "collapse",
    name: "Notifications",
    key: "notifications",
    icon: <Icon fontSize="small">notifications</Icon>,
    route: "/notifications",
    component: (
      <PrivateRoute>
        <Notifications />
      </PrivateRoute>
    ),
  },
  {
    type: "collapse",
    name: "Contest Management",
    key: "contest",
    icon: <Icon fontSize="small">emoji_events</Icon>,
    route: "/contest",
    component: (
      <PrivateRoute>
        <Contest />
      </PrivateRoute>
    ),
  },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    icon: <Icon fontSize="small">settings</Icon>,
    route: "/settings",
    component: (
      <PrivateRoute>
        <Settings />
      </PrivateRoute>
    ),
  },
  {
    type: "route",
    name: "Profile",
    key: "profile",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/profile",
    component: <Profile />,
  },
  {
    type: "route",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/sign-in",
    component: (
      <PublicRoute>
        <SignIn />
      </PublicRoute>
    ),
  },
];

export default routes;
